import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Activity,
  Button,
  ForEach,
  Icon,
  MessageDisplay,
  MessageEditor,
  MessageEditorSubmitParams,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import { useCounterContext } from '../hooks/useCounter';
import {
  ChatConversation,
  ChatMessage,
  ChatMessageTiptapAttributes,
  useChatContext,
  useChatMessages,
  useLastMessage,
  useParticipantFromMessage,
  useUserFromParticipant,
  useConversationById,
  useConversationUsers,
  useUser,
} from '../../Chat';
import {
  Counter,
  CounterActivity,
  FeedActivity,
  FeedBackMessage,
  FeedItem,
  FeedMessage,
} from '../types';
import { useCounterActivities, useCounterChat } from '../hooks/queries';
import { ChatUserTooltipContent } from '../../Chat/ChatUserTooltip';
import formattedDateMonthDayYearUTC from '../../../Lib/Utils/Date/formattedDateMonthDayYearUTC';
import { ChatBackMessage } from '../../PurchaseOrderDetail/PurchasrOrderActivities/ChatBackMessage';

function ConversationViewInput(props: { conversation: ChatConversation }) {
  const { conversation } = props;
  const { userId, identity } = useChatContext();

  const usersProcess = useConversationUsers(userId, conversation.instance);

  const queryUsers = (query: string) => {
    const users = usersProcess.map((u) => ({
      id: u.data?.identity || '',
      name: u.data?.friendlyName || '',
      type:
        u.data?.attributes.type === 'vp'
          ? ('vendor' as const)
          : ('fn_internal' as const),
    }));
    return users.filter((u) =>
      u.name.toLowerCase().includes(query.toLowerCase()),
    );
  };

  const sendMessage = useCallback(
    async (params: MessageEditorSubmitParams) => {
      const attributes: ChatMessageTiptapAttributes = {
        type: 'tiptap',
        preview: params.text.slice(0, 50),
      };

      const message = conversation.instance
        .prepareMessage()
        .setBody(JSON.stringify(params.json))
        .setAttributes(attributes);

      params.files.forEach((file, index) => {
        if (file.size === 0) {
          throw new Error('You cannot send an empty file.');
        }
        const formData = new FormData();
        formData.append(`file-${index + 1}`, file, file.name);
        message.addMedia(formData);
      });

      await message.build().send();
    },
    [conversation.instance],
  );

  return (
    <div className="flex-none p-4 border-t border-neutral-300 w-full">
      <MessageEditor
        onSubmit={sendMessage}
        placeholder="Leave a message"
        mentionUser={{
          userId: identity,
          queryUsers,
          className: 'z-50',
        }}
        fileOptions={{
          maximumFiles: 10,
        }}
      />
    </div>
  );
}

function Important(props: { children: React.ReactNode }) {
  return (
    <span className="font-bold nu-text-base-primary">{props.children}</span>
  );
}

const activityMessage = (
  activity: CounterActivity,
  vendorName: string,
  isFirstTimeSubmission?: boolean,
) => {
  const type = activity.event_type;

  if (type === 'EDIT_COUNTER_QUOTE' || type === 'EDIT_VENDOR_COUNTER_QUOTE') {
    return isFirstTimeSubmission ? (
      <>
        submitted the <Important>Quote</Important>
      </>
    ) : (
      <>
        edited the <Important>Quote</Important>
      </>
    );
  }

  if (
    type === 'UPDATE_COUNTER_CONTACTS' ||
    type === 'UPDATE_VENDOR_COUNTER_CONTACTS'
  ) {
    const addedArray =
      type === 'UPDATE_COUNTER_CONTACTS'
        ? [
            ...activity.event_params.added_fn_contacts,
            ...activity.event_params.added_vendor_contacts,
            ...(activity.event_params.added_primary_contact
              ? [activity.event_params.added_primary_contact]
              : []),
          ]
        : activity.event_params.added_contacts;
    const removedArray =
      type === 'UPDATE_COUNTER_CONTACTS'
        ? [
            ...activity.event_params.removed_fn_contacts,
            ...activity.event_params.removed_vendor_contacts,
            ...(activity.event_params.removed_primary_contact
              ? [activity.event_params.removed_primary_contact]
              : []),
          ]
        : activity.event_params.removed_contacts;

    if (!addedArray.length && !removedArray.length) {
      return undefined;
    }

    const addedMessage =
      addedArray.length > 0 ? (
        <>
          added{' '}
          <Important>{addedArray.map((c) => c.full_name).join(', ')}</Important>{' '}
          to the chat
        </>
      ) : undefined;
    const removedMessage =
      removedArray.length > 0 ? (
        <>
          removed{' '}
          <Important>
            {removedArray.map((c) => c.full_name).join(', ')}
          </Important>{' '}
          from the chat
        </>
      ) : undefined;
    return (
      <>
        {addedMessage}
        {addedMessage && removedMessage ? ' and ' : ''}
        {removedMessage}
      </>
    );
  }

  if (type === 'SEND_COUNTER_QUOTE_REQUEST_TO_VENDOR') {
    return (
      <>
        sent the <Important>Quote Request</Important> to{' '}
        <Important>{vendorName}</Important>
      </>
    );
  }

  if (type === 'APPROVE_VENDOR_FOR_COUNTER') {
    return (
      <>
        marked the <Important>Quote</Important> as{' '}
        <Important>Approved</Important>
      </>
    );
  }

  if (type === 'REJECT_VENDOR_FOR_COUNTER') {
    return (
      <>
        marked the <Important>Quote</Important> as{' '}
        <Important>Rejected</Important>
      </>
    );
  }

  if (type === 'MARK_PREV_SAMPLE_AS_VENDOR_RECEIVED') {
    return (
      <>
        updated the status of <Important>Previous Sample</Important> to{' '}
        <Important>
          Received by {vendorName} on{' '}
          {formattedDateMonthDayYearUTC(
            activity.event_params.vendor_received_at,
          )}
        </Important>
      </>
    );
  }

  if (type === 'SHIP_PREV_SAMPLE_TO_VENDOR') {
    return (
      <>
        updated the status of <Important>Previous Sample</Important> to{' '}
        <Important>
          Shipped to {vendorName} on{' '}
          {formattedDateMonthDayYearUTC(activity.event_params.shipped_at)}
        </Important>
      </>
    );
  }

  if (
    type === 'MARK_COUNTER_SAMPLE_AS_SHIPPED' ||
    type === 'MARK_VENDOR_COUNTER_SAMPLE_AS_SHIPPED'
  ) {
    return (
      <>
        marked the <Important>Vendor Sample</Important> as{' '}
        <Important>
          Shipped to FN on{' '}
          {formattedDateMonthDayYearUTC(activity.event_params.shipped_at)}
        </Important>
      </>
    );
  }

  if (type === 'MARK_COUNTER_SAMPLE_AS_RECEIVED') {
    return (
      <>
        marked the <Important>Vendor Sample</Important> as{' '}
        <Important>
          Received by FN on{' '}
          {formattedDateMonthDayYearUTC(activity.event_params.received_at)}
        </Important>
      </>
    );
  }

  return undefined;
};

function ConversationViewActivity(props: {
  activity: CounterActivity;
  vendorName: string;
  isLast: boolean;
  isFirstTimeSubmission?: boolean;
}) {
  const { activity, vendorName, isLast, isFirstTimeSubmission } = props;
  const { client, userId } = useChatContext();

  const message = activityMessage(activity, vendorName, isFirstTimeSubmission);

  const userProcess = useUser(
    client,
    userId,
    `${activity.author.type}_${activity.author.id}`,
  );

  if (!message) {
    return null;
  }

  const badgeType = activity.author.type === 'vp' ? 'vendor' : 'fn_internal';

  return (
    <Activity
      name={activity.author.name}
      badgeType={badgeType}
      hideBar={isLast}
      tooltipContent={
        <ChatUserTooltipContent
          name={activity.author.name}
          chatUser={userProcess.data}
          badgeType={badgeType}
        />
      }
      tooltipZIndex={50}
    >
      <Activity.Event
        name={activity.author.name}
        date={activity.created_at}
        tooltipZIndex={50}
      >
        {message}
      </Activity.Event>
    </Activity>
  );
}

export function ConversationViewMessage(props: {
  message: ChatMessage;
  isLast: boolean;
}) {
  const { message, isLast } = props;
  const { userId, identity } = useChatContext();

  const participantProcess = useParticipantFromMessage(
    userId,
    message.instance,
  );
  const userProcess = useUserFromParticipant(
    userId,
    participantProcess.data?.instance,
  );

  const isVendor =
    userProcess?.data?.attributes.type === 'vp' &&
    ['VENDOR', 'VENDOR_STAFF'].includes(userProcess.data.attributes.role);
  const isInfluencer =
    userProcess?.data?.attributes.type === 'vp' &&
    ['INFLUENCER'].includes(userProcess.data.attributes.role);
  const isInternal =
    userProcess?.data?.attributes.type === 'internal' ||
    (userProcess?.data?.attributes.type === 'vp' &&
      !['VENDOR', 'VENDOR_STAFF', 'INFLUENCER'].includes(
        userProcess.data.attributes.role,
      ));

  const badge =
    (isVendor && 'vendor') ||
    (isInfluencer && 'influencer') ||
    (isInternal && 'fn_internal') ||
    undefined;

  return (
    <Activity
      name={userProcess.data?.friendlyName || ''}
      badgeType={badge}
      hideBar={isLast}
      tooltipContent={
        <ChatUserTooltipContent
          name=""
          chatUser={userProcess.data}
          badgeType={badge}
        />
      }
      tooltipZIndex={50}
    >
      <Activity.Message
        name={userProcess.data?.friendlyName || ''}
        date={message.instance.dateCreated || new Date()}
        isPersonal={identity === participantProcess.data?.identity}
        tooltipZIndex={50}
      >
        {message.attributes.type === 'tiptap' ? (
          <MessageDisplay
            content={message.body}
            files={message.files}
            mentionUser={{ userId: identity }}
          />
        ) : (
          <P>{message.body}</P>
        )}
      </Activity.Message>
    </Activity>
  );
}

function ConversationView(props: { counter: Counter }) {
  const { counter } = props;

  const { client, userId, status } = useChatContext();
  const isConnected = client && status === 'connected';

  const conversationProcess = useConversationById(
    userId,
    isConnected ? client : undefined,
    'counter',
    counter.counter_id,
  );

  const conversation = !conversationProcess.isError
    ? conversationProcess.data
    : undefined;

  const messagesProcess = useChatMessages(userId, conversation?.instance);

  const backendMessagesProcess = useCounterChat(userId, counter.counter_id);
  const activitiesProcess = useCounterActivities(userId, counter.counter_id);

  const feed: FeedItem[] = useMemo(() => {
    const { messages } = messagesProcess;
    const activities = activitiesProcess.data || [];
    const backMessages = !messages?.length
      ? backendMessagesProcess.data || []
      : [];

    let combined: FeedItem[] = [
      ...messages.map<FeedMessage>((m) => ({
        type: 'message',
        message: m,
      })),
      ...backMessages.map<FeedBackMessage>((m) => ({
        type: 'back-message',
        message: m,
      })),
      ...activities.map<FeedActivity>((a) => ({
        type: 'activity',
        activity: a,
      })),
    ].sort((a, b) => {
      const dateA =
        // eslint-disable-next-line no-nested-ternary
        a.type === 'message'
          ? a.message.instance.dateCreated || new Date()
          : a.type === 'back-message'
            ? new Date(a.message.dateCreated)
            : new Date(a.activity.created_at);
      const dateB =
        // eslint-disable-next-line no-nested-ternary
        b.type === 'message'
          ? b.message.instance.dateCreated || new Date()
          : b.type === 'back-message'
            ? new Date(b.message.dateCreated)
            : new Date(b.activity.created_at);
      return dateA.getTime() - dateB.getTime();
    });

    const encounteredEventTypes = new Set<string>();

    combined = combined.map((item) => {
      if (item.type === 'activity') {
        const { event_type: eventType } = item.activity;

        const isCounterQuoteEvent =
          eventType === 'EDIT_COUNTER_QUOTE' ||
          eventType === 'EDIT_VENDOR_COUNTER_QUOTE';

        if (isCounterQuoteEvent) {
          const eventKey = 'COUNTER_QUOTE';

          const isFirstTimeSubmission = !encounteredEventTypes.has(eventKey);
          encounteredEventTypes.add(eventKey);

          return {
            ...item,
            isFirstTimeSubmission,
          };
        }
      }
      return item;
    });

    return combined;
  }, [
    messagesProcess.messages,
    activitiesProcess.data,
    backendMessagesProcess.data,
  ]);

  const lastMessageProcess = useLastMessage(userId, conversation?.instance);
  useEffect(() => {
    if (
      conversation &&
      lastMessageProcess.data?.instance.index &&
      (!conversation.instance.lastReadMessageIndex ||
        conversation.instance.lastReadMessageIndex <
          lastMessageProcess.data.instance.index)
    ) {
      conversation.instance.updateLastReadMessageIndex(
        lastMessageProcess.data.instance.index,
      );
    }
  }, [
    lastMessageProcess.data?.instance.index,
    conversation?.instance.lastReadMessageIndex,
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'instant' as ScrollBehavior, // As is necessary as we have an old TS version that doesn't know about 'instant'
    });
  }, []);

  useEffect(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'instant' as ScrollBehavior, // As is necessary as we have an old TS version that doesn't know about 'instant'
      });
      isProgrammaticScrollRef.current = true;
    }
  }, [
    messagesProcess.messages?.length,
    backendMessagesProcess.data?.length,
    activitiesProcess.data?.length,
    !!conversation,
  ]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    isAtBottomRef.current = scrollHeight - (scrollTop + clientHeight) < 40; // Tolerance of 40px
  };

  return (
    <>
      <div
        className="flex-grow overflow-y-scroll overflow-x-hidden p-4 px-8 flex flex-col gap-1"
        onScroll={handleScroll}
      >
        {messagesProcess.hasPreviousPage && (
          <div className="w-full flex justify-center p-4">
            <Button
              loading={messagesProcess.isFetchingPreviousPage}
              onClick={() => messagesProcess.fetchPreviousPage()}
            >
              Load more messages
            </Button>
          </div>
        )}
        <ForEach items={feed}>
          {(feedItem, index) => {
            if (feedItem.type === 'message') {
              return (
                <ConversationViewMessage
                  key={feedItem.message.sid}
                  message={feedItem.message}
                  isLast={index === feed.length - 1}
                />
              );
            }
            if (feedItem.type === 'back-message') {
              return (
                <ChatBackMessage
                  key={feedItem.message.sid}
                  message={feedItem.message}
                  isLast={index === feed.length - 1}
                />
              );
            }
            return (
              <ConversationViewActivity
                key={feedItem.activity.event_id}
                activity={feedItem.activity}
                vendorName={counter.counter_vendor?.vendor_name || ''}
                isLast={index === feed.length - 1}
                isFirstTimeSubmission={feedItem.isFirstTimeSubmission}
              />
            );
          }}
        </ForEach>
        {(activitiesProcess.isLoading || backendMessagesProcess.isLoading) && (
          <Icon.LoadingCircle />
        )}
        <span ref={messagesEndRef} />
      </div>
      <div className="w-full flex justify-center">
        {conversation && <ConversationViewInput conversation={conversation} />}
        {!client || status !== 'connected' ? (
          <P color="error">You are not connected to chat service</P>
        ) : null}
        {client && status === 'connected' && !conversation && (
          <P color="disabled">
            Chat is inactive or out of sync. Update members to reactivate chat.
          </P>
        )}
      </div>
    </>
  );
}

function ChatForbidden() {
  return (
    <div className="flex flex-col gap-3 flex-grow items-center justify-center p-8">
      <P size="xl" color="purple">
        <Icon name="chat/chat-default" />
      </P>
      <div>
        <P size="xl" color="primary" className="text-center">
          You&apos;re not part of this conversation yet.
        </P>
        <P size="sm" color="secondary" className="text-center">
          Joining will notify all members that you&apos;ve joined. After
          joining, you&apos;ll have access to past messages and can send new
          ones.
        </P>
      </div>
    </div>
  );
}

function Chat() {
  const { selectedCounter } = useCounterContext();
  const { userId } = useChatContext();

  if (!selectedCounter) {
    return <div className="p-5 flex justify-center">No counter selected</div>;
  }

  if (!selectedCounter.vendor_contacts?.some((c) => c.id === userId)) {
    return <ChatForbidden />;
  }

  return (
    <ConversationView
      key={selectedCounter.counter_id}
      counter={selectedCounter}
    />
  );
}

export default Chat;
